import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom, type PropsOf } from '@blaze-runtime/engine-domain';

import { getSourceTransforms } from '../../canvas-elements/element-model/transformations';

type ImageElement = CanvasElementFrom<'image'>;
type ImageProps = PartialDeep<PropsOf<'image'>>;

function transformDropdownToImage(source: CanvasElementFrom<'dropdown'>): ImageElement {
  const props: PartialDeep<PropsOf<'dropdown'>> = source.props || {};
  const newProps: ImageProps = {
    content: {
      imageSource: {
        enabled: undefined, // no mapping
        url: undefined, // no mapping
      },
      options: {
        altText: undefined, // no mapping
        behavior: undefined, // no mapping
        readonly: undefined, // no mapping
      },
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
      },
      outline: {
        color: props.style?.input?.border,
        width: undefined, // no mapping
        radius: props.style?.input?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformRadioToImage(source: CanvasElementFrom<'radio'>): ImageElement {
  const props: PartialDeep<PropsOf<'radio'>> = source.props || {};
  const newProps: ImageProps = {
    content: {
      imageSource: {
        enabled: undefined, // no mapping
        url: undefined, // no mapping
      },
      options: {
        altText: undefined, // no mapping
        behavior: undefined, // no mapping
        readonly: props.content?.radio?.readonly,
      },
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
      },
      outline: {
        color: props.style?.radio?.border,
        width: undefined, // no mapping
        radius: undefined, // no mapping
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformTextInputToImage(source: CanvasElementFrom<'textInput'>): ImageElement {
  const props: PartialDeep<PropsOf<'textInput'>> = source.props || {};
  const newProps: ImageProps = {
    content: {
      imageSource: {
        enabled: undefined, // no mapping
        url: undefined, // no mapping
      },
      options: {
        altText: undefined, // no mapping
        behavior: undefined, // no mapping
        readonly: props.content?.input?.readonly,
      },
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align?.horizontal,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
      },
      outline: {
        color: props.style?.input?.border,
        width: undefined, // no mapping
        radius: props.style?.input?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformTextFieldToImage(source: CanvasElementFrom<'textField'>): ImageElement {
  const props: PartialDeep<PropsOf<'textField'>> = source.props || {};
  const propsT = getSourceTransforms<'textField', 'image'>(source, 'image');
  const newProps: ImageProps = {
    content: {
      imageSource: propsT?.content?.imageSource,
      options: {
        ...propsT?.content?.options,
        readonly: true,
      },
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align?.horizontal,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      link: propsT?.content?.link,
    },
    style: {
      fontSize: props.style?.fontSize,
      fontFamily: props.style?.fontFamily,
      lineHeight: props.style?.lineHeight,
      image: propsT?.style?.image,
      general: {
        title: props.style?.general?.title,
      },
      outline: {
        color: props.style?.input?.border,
        width: propsT?.style?.outline?.width,
        radius: props.style?.input?.radius,
      },
    },
    transforms: {
      textField: {
        content: {
          title: {
            align: props.content?.title?.align,
          },
          input: props.content?.input,
          container: {
            overflow: props.content?.container?.overflow,
          },
        },
        style: {
          general: {
            accent: props.style?.general?.accent,
            offset: props.style?.general?.offset,
          },
          inputFont: props.style?.inputFont,
          input: props.style?.input,
        },
      } as PartialDeep<PropsOf<'textField'>>,
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformUploadFileToImage(source: CanvasElementFrom<'uploadFile'>): ImageElement {
  const props: PartialDeep<PropsOf<'uploadFile'>> = source.props || {};
  const newProps: ImageProps = {
    content: {
      imageSource: {
        enabled: undefined, // no mapping
        url: undefined, // no mapping
      },
      options: {
        altText: undefined, // no mapping
        behavior: undefined, // no mapping
        readonly: props.content?.button?.readonly,
      },
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
      },
      outline: {
        color: props.style?.upload?.label,
        width: props.style?.button?.outlineWidth,
        radius: props.style?.button?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

export const transformToImage = {
  transformDropdownToImage,
  transformRadioToImage,
  transformTextInputToImage,
  transformTextFieldToImage,
  transformUploadFileToImage,
};
